<script setup lang="ts">
import { cva } from 'class-variance-authority'
import useUserStore from '~~/app/store/user'
import { ServiceMessageType } from '~~/interfaces/UI'
import { IconGift } from '#components'
import DOMPurify from 'isomorphic-dompurify'
import usePoolStore from '~/store/pool'

export interface Props {
    textKey?: string
    text?: string
    type?: ServiceMessageType
    iconName?: string
    displayOrderPass?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    textKey: '',
    text: '',
    type: ServiceMessageType.Info,
    iconName: '',
    displayOrderPass: false
})

const { t } = useI18n()
const poolStore = usePoolStore()

const icons: Record<string, Component> = { IconGift: IconGift }

const displayMoreButton = ref(true)
const displayButton = ref(false)

const serviceMessageText = useTemplateRef<HTMLElement>('serviceMessageText')

const customStyle = computed<string>(() =>
    cva(
        'flex justify-start items-start gap-2 p-4 mb-5 rounded-lg w-full font-normal text-base leading-[120%] tracking-tighter flex-[4]',
        {
            variants: {
                type: {
                    [ServiceMessageType.Info]: 'bg-sky-100 text-sky-800',
                    [ServiceMessageType.Positive]:
                        'bg-green-100 text-green-800',
                    [ServiceMessageType.Negative]: 'bg-red-100 text-red-800'
                }
            }
        }
    )({ type: props.type })
)

const iconStyle = computed<string>(() =>
    cva('min-w-4 h-4', {
        variants: {
            type: {
                [ServiceMessageType.Info]: 'text-sky-500',
                [ServiceMessageType.Positive]: 'text-green-500',
                [ServiceMessageType.Negative]: 'text-red-500'
            }
        }
    })({ type: props.type })
)

const sanitizedMessage = computed<string>(() =>
    DOMPurify.sanitize(t('discounted_networks.service_message.order_pass'))
)

const toggleMoreButton = () => {
    displayMoreButton.value = !displayMoreButton.value
}

const onClickOnOrderPass = () => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: 'pass/purchase/step1' },
            document.referrer
        )
    } else {
        return navigateTo(getChargemapUrl('pass/purchase/step1'), {
            external: true
        })
    }
}

onMounted(() => {
    if (serviceMessageText.value) {
        displayButton.value = serviceMessageText.value.scrollHeight > 20
    }
})
</script>

<template>
    <div class="flex flex-col justify-center items-end gap-2">
        <div :class="customStyle">
            <component
                :is="icons[iconName]"
                v-if="iconName"
                :class="iconStyle"
            />
            <div class="overflow-hidden">
                <i18n-t
                    v-if="text || textKey"
                    ref="serviceMessageText"
                    tag="p"
                    :keypath="text || textKey"
                    :class="[
                        displayMoreButton ? 'line-clamp-1' : 'line-clamp-none'
                    ]"
                >
                    <template #amount>
                        {{ poolStore.getDiscountAmount }}
                    </template>
                    <template #date>
                        {{ poolStore.getDiscountEndDate }}
                    </template>
                    <template #br>
                        <br />
                    </template>
                </i18n-t>
                <ChargemapB2CButton
                    v-if="
                        !useUserStore().userAlreadyHasAPass &&
                        !displayMoreButton &&
                        displayButton &&
                        displayOrderPass
                    "
                    type="tertiary"
                    size="medium"
                    class="mt-4"
                    @click="onClickOnOrderPass"
                >
                    <!--                    eslint-disable-next-line-->
                    <span v-html="sanitizedMessage" />
                </ChargemapB2CButton>
            </div>
            <div
                v-if="displayButton"
                class="cursor-pointer self-end text-fifteen-exploitation font-semibold"
                @click="toggleMoreButton"
            >
                {{
                    displayMoreButton
                        ? $t('show.more_short')
                        : $t('show.less_short')
                }}
            </div>
        </div>
    </div>
</template>
